import { PageHero } from "@/components/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/motion";
import { MultiStepOpinionForm } from "@/components/MultiStepOpinionForm";
import { LeafMark } from "@/components/LeafMark";
import { usePageMeta } from "@/lib/seo";
import { Lock, HeartHandshake, MessagesSquare } from "lucide-react";

export const GetMedicalOpinion = () => {
    const { t, tHtml } = useLang();
    const REASSURANCES = [
        {
            icon: Lock,
            title: t("opinion.r1.title", "Confidential by design"),
            desc: t("opinion.r1.desc", "Your information is handled discreetly and shared only with the specialists involved in your care."),
        },
        {
            icon: HeartHandshake,
            title: t("opinion.r2.title", "Guided, never rushed"),
            desc: t("opinion.r2.desc", "A real person reviews every case. We help you understand options before you decide anything."),
        },
        {
            icon: MessagesSquare,
            title: t("opinion.r3.title", "Simple, clear communication"),
            desc: t("opinion.r3.desc", "Talk to us in your preferred language, on the channel that suits you — including WhatsApp."),
        },
    ];
    usePageMeta(
        t("meta.get_medical_opinion.title", "Get a Medical Opinion"),
        t("meta.get_medical_opinion.desc", "Start your healthcare journey with clarity — share your case with UniversHeal through a simple five-step enquiry and we will coordinate the right specialist in Kerala, India.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("opinion.eyebrow", "Get a Medical Opinion")}
                title={tHtml(
                    "opinion.title",
                    <>
                        Start with{" "}
                        <span className="italic text-pine">clarity.</span>
                    </>
                )}
                intro={t("opinion.intro", "Tell us about your healthcare needs. Our coordination team will help you understand your options and connect with the right specialist in Kerala. Five short steps — no medical knowledge needed.")}
            />

            <section className="pb-10">
                <div className="container-site">
                    <div className="grid sm:grid-cols-3 gap-5">
                        {REASSURANCES.map((item, i) => (
                            <Reveal key={item.title} delay={i * 0.07} className="h-full">
                                <div
                                    data-testid={`opinion-reassurance-${i + 1}`}
                                    className="h-full bg-white border border-pine/10 rounded-2xl p-7"
                                >
                                    <item.icon className="w-6 h-6 text-forest" strokeWidth={1.5} />
                                    <h3 className="mt-5 font-serif font-medium text-xl text-forest">
                                        {item.title}
                                    </h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-moss">
                                        {item.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section-pad pt-6">
                <div className="container-site max-w-4xl">
                    <Reveal>
                        <MultiStepOpinionForm />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-8 text-xs leading-relaxed text-moss flex items-start gap-2 max-w-2xl">
                            <LeafMark className="w-3.5 h-3.5 mt-0.5 text-natural shrink-0" />
                            {t("opinion.disclaimerNote", "UniversHeal coordinates access to healthcare services and does not itself diagnose or provide medical treatment. If you are experiencing a medical emergency, please contact your local emergency services immediately.")}
                        </p>
                    </Reveal>
                </div>
            </section>
        </>
    );
};
